import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { propTypesGatsbyImage } from '../../../shared/constants/Proptypes';

import { useFirebaseLikes } from '../../../shared/hooks/useFirebaseLikes';
import { useLocalStorageArray } from '../../../shared/hooks/useLocaleStorageArray';
import { addTrackingParamsToUrl } from '../../../shared/utils/trackingParams';
import { truncateString } from '../../../shared/utils/truncateString';

import { Avatar, AvatarSize } from '../../Atoms/Avatar/Avatar';
import Button, { ButtonAppearance } from '../../Atoms/Button';
import Like from '../../Atoms/Like';
import Picture from '../../Atoms/Picture';

import * as S from './EventCard.styles';

const STORAGE_KEY = 'favoriteTemplates';

const PictureContainer = ({ isHighlighted, pictureProps }) => {
  if (!pictureProps.imageUrl) {
    return null;
  }

  return (
    <S.PictureContainer isHighlighted={isHighlighted}>
      <Picture {...pictureProps} />
    </S.PictureContainer>
  );
};

const TitleContainer = ({
  isHighlighted,
  title,
  description,
  author,
  authorJob,
  authorProfilePictureProps,
}) => {
  if (!title || !description) {
    return null;
  }

  const truncatedTitle = truncateString(title, 65);
  const truncatedDescription = truncateString(description, 200);

  return (
    <>
      <S.TitleCard isHighlighted={isHighlighted}>{truncatedTitle}</S.TitleCard>
      <S.Description>{truncatedDescription}</S.Description>
      <S.AuthorWrapper>
        <Avatar {...authorProfilePictureProps} />
        <S.AuthorTitleWrapper>
          <S.AuthorFullName>{author}</S.AuthorFullName>
          <S.AuthorJob>{authorJob}</S.AuthorJob>
        </S.AuthorTitleWrapper>
      </S.AuthorWrapper>
    </>
  );
};

const ButtonContainer = ({
  isHighlighted,
  actionLabel,
  watchLabel,
  eventSlug,
  url,
  handleClick,
  isClicked,
}) => {
  if (!actionLabel || !watchLabel || !eventSlug || !url || !handleClick) {
    return null;
  }

  const importLink = `https://app.wooclap.com/auth/register?redirectTo=/home/import?slug=${eventSlug}%2526confirm=true`;

  const [importUrl, setImportUrl] = useState(importLink);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setImportUrl(addTrackingParamsToUrl(importLink));
    }, 500);

    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const handleCookieChange = () => {
      setImportUrl(addTrackingParamsToUrl(importLink));
    };

    document.addEventListener('cookiechange', handleCookieChange);

    return () => {
      document.removeEventListener('cookiechange', handleCookieChange);
    };
  }, []);

  return (
    <S.ButtonContainer isHighlighted={isHighlighted}>
      <Button altText={actionLabel} link={importUrl} target='_blank'>
        {actionLabel}
      </Button>
      <S.DetailWrapper>
        <Button
          altText={watchLabel}
          appearance={ButtonAppearance.OUTLINE}
          link={url}
        >
          {watchLabel}
        </Button>
      </S.DetailWrapper>
      <S.LikeWrapper>
        <Like handleClick={handleClick} isClicked={isClicked} />
      </S.LikeWrapper>
    </S.ButtonContainer>
  );
};

const BottomContainer = ({
  isHighlighted,
  numberOfQuestion,
  questions,
  question,
  authorCompany,
  authorProfilePictureProps,
  author,
  isClicked,
  likes,
}) => {
  if (!numberOfQuestion || !questions || !question || !author) {
    return null;
  }

  return (
    <S.BottomContainer isHighlighted={isHighlighted}>
      <Avatar size={AvatarSize.SMALL} {...authorProfilePictureProps} />
      <S.AuthorNameJobWrapper>
        <S.Author>{`${author}`}</S.Author>
        {authorCompany && <S.AuthorCompany>{authorCompany}</S.AuthorCompany>}
      </S.AuthorNameJobWrapper>
      <S.QuestionsLikesWrapper>
        <S.QuestionCounter>{`${numberOfQuestion} ${
          numberOfQuestion > 1 ? questions : question
        }`}</S.QuestionCounter>
        <Like isClicked={isClicked} isCounter={true} likes={likes} />
      </S.QuestionsLikesWrapper>
    </S.BottomContainer>
  );
};

export const EventCard = ({
  actionLabel,
  author,
  authorJob,
  authorProfilePictureProps,
  authorCompany,
  description,
  eventSlug,
  isHighlighted,
  numberOfQuestion,
  pictureProps,
  question,
  questions,
  title,
  url,
  watchLabel,
}) => {
  if (
    !pictureProps?.imageUrl ||
    !description ||
    !title ||
    !eventSlug ||
    !actionLabel ||
    !watchLabel ||
    !url
  ) {
    return null;
  }

  // Firebase hook
  const { likes, getLikes, incrementLikes, decrementLikes } =
    useFirebaseLikes(eventSlug);

  // LocalStorageArray hook
  const [data, addItem, removeItem, updateItem, clearItems, getItem, getItems] =
    useLocalStorageArray(STORAGE_KEY, []);

  /**
   * Get the current template object from the localStorage
   * in order to know if it's already liked or not.
   * If the current template is not yet in the localStorage,
   * it returns a default object with isFavorite = false.
   * @returns {{id: string, timestamp: number, isFavorite: boolean}}
   */
  const initialState = () => {
    const itemIndex = data?.findIndex(item => item.id === eventSlug);

    if (itemIndex !== -1) {
      return getItem(itemIndex);
    } else {
      return {
        id: eventSlug,
        timestamp: Date.now(),
        isFavorite: false,
      };
    }
  };

  // Local state of the template object.
  const [currentLikedItem, setLikedItem] = useState(initialState());

  /**
   * Create a version of the template object
   * with a timestamp corresponding to now.
   * @param {number} newTimestamp
   */
  const setLocalItem = newTimestamp => {
    setLikedItem({
      id: eventSlug,
      timestamp: newTimestamp,
      isFavorite: !currentLikedItem.isFavorite,
    });
  };

  /**
   * Increments or decrements the amount of like
   * depending on if the template is already liked
   * or not.
   * @param {boolean} isFavorite
   */
  const updateLikeAmount = isFavorite => {
    if (isFavorite) {
      decrementLikes();
    } else {
      incrementLikes();
    }
  };

  /**
   * Update an existing template in the localStorage
   * at the given index with the given timestamp
   * and update the amount of likes in Firebase.
   * @param {number} itemIndex
   * @param {number} now
   */
  const updateExistingItem = (itemIndex, now) => {
    const currentItem = getItem(itemIndex);

    updateLikeAmount(currentItem.isFavorite);
    const newItem = {
      id: eventSlug,
      timestamp: now,
      isFavorite: !currentItem.isFavorite,
    };

    updateItem(itemIndex, newItem);
    setLocalItem(now);
  };

  /**
   * Create a new template in the localStorage
   * with the given timestamp and update the
   * amount of likes in Firebase.
   * @param {number} now
   */
  const createNewItem = now => {
    updateLikeAmount(currentLikedItem.isFavorite);

    addItem({
      id: eventSlug,
      timestamp: now,
      isFavorite: !currentLikedItem.isLiked,
    });

    setLocalItem(now);
  };

  const handleClick = () => {
    getItems();
    const now = Date.now();
    const itemIndex = data?.findIndex(item => item.id === eventSlug);

    if (itemIndex !== -1) {
      updateExistingItem(itemIndex, now);
    } else {
      createNewItem(now);
    }
  };

  const isClicked = currentLikedItem?.isFavorite || false;

  const buttonProps = {
    isHighlighted,
    watchLabel,
    actionLabel,
    eventSlug,
    url,
    handleClick,
    isClicked,
  };

  const bottomProps = {
    isHighlighted,
    numberOfQuestion,
    questions,
    question,
    authorCompany,
    authorProfilePictureProps,
    author,
    isClicked,
    likes,
  };

  const titleProps = {
    isHighlighted,
    title,
    description,
    author,
    authorJob,
    authorProfilePictureProps,
  };

  const pictureContainerProps = {
    isHighlighted,
    pictureProps,
  };

  return (
    <>
      {isHighlighted ? (
        <S.HorizontalCard>
          <S.HorizontalWrapper>
            <PictureContainer {...pictureContainerProps} />
            <S.ContentContainer>
              <TitleContainer {...titleProps} />
              <BottomContainer {...bottomProps} />
              <ButtonContainer {...buttonProps} />
            </S.ContentContainer>
          </S.HorizontalWrapper>
        </S.HorizontalCard>
      ) : (
        <S.Card>
          <PictureContainer {...pictureContainerProps} />
          <TitleContainer {...titleProps} />
          <BottomContainer {...bottomProps} />
          <ButtonContainer {...buttonProps} />
        </S.Card>
      )}
    </>
  );
};

EventCard.propTypes = {
  /**
   * Label of the Import CTA.
   */
  actionLabel: PropTypes.string.isRequired,
  /**
   * Author picture props
   */
  authorProfilePictureProps: PropTypes.shape({
    imageObject: PropTypes.shape(propTypesGatsbyImage),
    imageUrl: PropTypes.string,
  }),
  /**
   * Event creator full name.
   */
  author: PropTypes.string,
  /**
   * Event creator profile picture.
   */
  authorProfilePictureProps: PropTypes.shape({
    altText: PropTypes.string,
    imageUrl: PropTypes.string,
  }),
  /**
   * Short description of the event displayed when
   * the user is hovering the card.
   */
  description: PropTypes.string.isRequired,
  /**
   * Event slug used for the import url.
   * 340 char max.
   */
  eventSlug: PropTypes.string.isRequired,
  /**
   * If isPopular is TRUE, the card will have an
   * horizontal design with a biger illustration and
   * more space for the description.
   */
  isHighlighted: PropTypes.bool,
  /**
   * The number of questions in the event template.
   */
  numberOfQuestion: PropTypes.number,
  /**
   * Event cover.
   */
  pictureProps: PropTypes.shape({
    imageObject: PropTypes.shape(propTypesGatsbyImage),
    imageUrl: PropTypes.string,
    altText: PropTypes.string,
    radius: PropTypes.string,
    width: PropTypes.string,
  }),
  /**
   * The singular copy for the "question" word.
   */
  question: PropTypes.string,
  /**
   * The plural copy for the "question" word.
   */
  questions: PropTypes.string,
  /**
   * The title of the event template.
   * 65 char max.
   */
  title: PropTypes.string.isRequired,
  /**
   * Url of the Event page.
   */
  url: PropTypes.string.isRequired,
};

EventCard.defaultProps = {
  isHighlighted: false,
  pictureAlt: 'Event illustration',
};
